/**
 * CreatorDashboardPage.tsx
 * ─────────────────────────────────────────────────────────────
 * 크리에이터 전용 대시보드
 * - 내 영상 목록 & 상태
 * - 조회수 / 유효 시청수
 * - 크레딧 누적 & 정산 신청
 */

import React, { useState } from 'react'
import {
  Award, Eye, Star, Clock, CheckCircle, XCircle,
  Youtube, TrendingUp, Banknote, AlertCircle,
  ChevronDown, ChevronUp, RefreshCw, Loader2
} from 'lucide-react'
import {
  useVideoStore, CREDIT_PER_VIEW, MIN_WATCH_SEC,
  ConceptVideo, VideoStatus,
} from '../store/videoStore'

// ─────────────────────────────────────────────────────────────
// 헬퍼
// ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: VideoStatus }) {
  const map = {
    pending:  { label: '검수 대기', cls: 'bg-amber-100 text-amber-700' },
    approved: { label: '게시 중',   cls: 'bg-emerald-100 text-emerald-700' },
    rejected: { label: '반려됨',    cls: 'bg-red-100 text-red-700' },
  }
  const { label, cls } = map[status]
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
}

function StarRow({ rating, count }: { rating: number; count: number }) {
  return (
    <div className="flex items-center gap-1">
      {[1,2,3,4,5].map(n => (
        <Star key={n} size={11}
          className={n <= Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'} />
      ))}
      <span className="text-xs text-slate-400 ml-1">
        {count > 0 ? `${rating.toFixed(1)} (${count})` : '없음'}
      </span>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// 영상 행 컴포넌트
// ─────────────────────────────────────────────────────────────
function VideoRow({ video }: { video: ConceptVideo }) {
  const [open, setOpen] = useState(false)
  const earned = video.validViewCount * CREDIT_PER_VIEW

  return (
    <div className="border-2 border-slate-200 rounded-2xl overflow-hidden bg-white hover:border-slate-300 transition-all">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full text-left p-4 flex items-center gap-3"
      >
        {/* 썸네일 */}
        <img
          src={video.thumbnailUrl}
          alt={video.title}
          className="w-16 h-11 object-cover rounded-xl flex-shrink-0"
          onError={e => { (e.target as HTMLImageElement).src = 'https://placehold.co/64x44/e2e8f0/94a3b8?text=No' }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <StatusBadge status={video.status} />
            <span className="text-xs text-slate-400 font-mono">{video.conceptId}</span>
          </div>
          <p className="text-sm font-bold text-slate-800 truncate">{video.title}</p>
          <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
            <span className="flex items-center gap-1"><Eye size={10} /> {video.viewCount}</span>
            <span className="flex items-center gap-1 text-emerald-600 font-semibold">
              <Award size={10} /> {earned.toLocaleString()}원
            </span>
          </div>
        </div>
        {open ? <ChevronUp size={14} className="text-slate-400 flex-shrink-0" />
               : <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 p-4 bg-slate-50/50">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            {[
              { label: '총 시청',   value: `${video.viewCount}회` },
              { label: '유효 시청', value: `${video.validViewCount}회` },
              { label: '적립 크레딧', value: `${earned.toLocaleString()}원`, highlight: true },
              { label: '평점',      value: <StarRow rating={video.rating} count={video.ratingCount} /> },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-3 border border-slate-200 text-center">
                <p className="text-xs text-slate-400 mb-1">{item.label}</p>
                {typeof item.value === 'string'
                  ? <p className={`text-sm font-black ${(item as any).highlight ? 'text-violet-700' : 'text-slate-700'}`}>{item.value}</p>
                  : <div className="flex justify-center">{item.value}</div>
                }
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">{video.description}</p>
          {video.status === 'rejected' && video.adminNote && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
              <AlertCircle size={12} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold text-red-600 mb-0.5">반려 사유</p>
                <p className="text-xs text-red-700">{video.adminNote}</p>
                <p className="text-xs text-red-500 mt-1">수정 후 재신청하시면 다시 검토해드립니다.</p>
              </div>
            </div>
          )}
          {video.youtubeUrl && (
            <a
              href={video.youtubeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-red-500 hover:underline"
            >
              <Youtube size={11} /> YouTube에서 보기
            </a>
          )}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// 정산 신청 모달
// ─────────────────────────────────────────────────────────────
function PayoutModal({
  pendingCredit,
  onClose,
}: {
  pendingCredit: number
  onClose: () => void
}) {
  const [bankName,   setBankName]   = useState('')
  const [accountNo,  setAccountNo]  = useState('')
  const [holderName, setHolderName] = useState('')
  const [step, setStep] = useState<'form' | 'done'>('form')
  const [loading, setLoading] = useState(false)

  const handleSubmit = () => {
    if (!bankName || !accountNo || !holderName) return
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
      setStep('done')
    }, 1000)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6">
        {step === 'done' ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={28} className="text-white" />
            </div>
            <h3 className="text-lg font-black text-slate-800 mb-2">정산 신청 완료!</h3>
            <p className="text-sm text-slate-500 mb-1">
              <span className="font-bold text-violet-700">{pendingCredit.toLocaleString()}원</span> 정산이 신청되었습니다.
            </p>
            <p className="text-xs text-slate-400 mb-6">영업일 기준 5일 이내 입금됩니다.</p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-bold hover:bg-slate-200 transition-colors"
            >
              닫기
            </button>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-black text-slate-800 mb-1">크레딧 정산 신청</h3>
            <p className="text-sm text-slate-500 mb-5">
              정산 가능 금액:{' '}
              <span className="font-black text-violet-700 text-base">{pendingCredit.toLocaleString()}원</span>
            </p>

            <div className="space-y-3 mb-5">
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">은행명</label>
                <input
                  value={bankName}
                  onChange={e => setBankName(e.target.value)}
                  placeholder="국민은행, 카카오뱅크 등"
                  className="w-full border-2 border-slate-200 focus:border-violet-400 rounded-xl px-3 py-2.5 text-sm outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">계좌번호</label>
                <input
                  value={accountNo}
                  onChange={e => setAccountNo(e.target.value)}
                  placeholder="'-' 없이 숫자만 입력"
                  className="w-full border-2 border-slate-200 focus:border-violet-400 rounded-xl px-3 py-2.5 text-sm outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 mb-1 block">예금주</label>
                <input
                  value={holderName}
                  onChange={e => setHolderName(e.target.value)}
                  placeholder="실명 입력"
                  className="w-full border-2 border-slate-200 focus:border-violet-400 rounded-xl px-3 py-2.5 text-sm outline-none"
                />
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 flex items-start gap-2">
              <AlertCircle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                3.3% 원천징수 후 지급됩니다. 연간 수입이 일정 금액 이상이면 종합소득세 신고가 필요합니다.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-colors text-sm"
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={!bankName || !accountNo || !holderName || loading}
                className="flex-1 py-2.5 rounded-xl bg-violet-500 text-white font-bold hover:bg-violet-600 transition-colors disabled:opacity-50 text-sm flex items-center justify-center gap-2"
              >
                {loading
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Banknote size={14} />}
                정산 신청
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────
// 메인 페이지
// ─────────────────────────────────────────────────────────────
const MIN_PAYOUT = 10_000   // 최소 정산 금액

export default function CreatorDashboardPage() {
  // 데모용: 첫 번째 크리에이터 시뮬레이션
  const { data, getCreatorStats } = useVideoStore(s => ({
    data: s.data,
    getCreatorStats: s.getCreatorStats,
  }))

  // 실제 환경에서는 로그인된 크리에이터 ID 사용
  const creator    = data.creators[0]
  const creatorId  = creator?.id ?? 'creator_seed_01'
  const stats      = getCreatorStats(creatorId)
  const myVideos   = data.videos.filter(v => v.creatorId === creatorId)

  const [showPayout, setShowPayout] = useState(false)
  const [filterStatus, setFilterStatus] = useState<'all' | VideoStatus>('all')

  const filtered = filterStatus === 'all'
    ? myVideos
    : myVideos.filter(v => v.status === filterStatus)

  const canPayout = stats.pendingCredit >= MIN_PAYOUT

  // 주간 시청 추이 (시뮬레이션 — 실제론 DB 집계)
  const weeklyViews = [0, 0, 0, 0, 0, 0, 0]
  const weekMax     = Math.max(...weeklyViews, 1)
  const dayLabels   = ['월', '화', '수', '목', '금', '토', '일']

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* 헤더 */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-md">
          <TrendingUp size={22} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-slate-800">크리에이터 대시보드</h1>
          <p className="text-sm text-slate-500">{creator?.name ?? '크리에이터'} 님 환영합니다</p>
        </div>
        <a
          href="/video-submit"
          className="ml-auto text-xs px-4 py-2 rounded-xl bg-violet-500 text-white font-bold hover:bg-violet-600 transition-colors"
        >
          + 영상 등록
        </a>
      </div>

      {/* 크레딧 요약 카드 */}
      <div className="bg-gradient-to-br from-violet-600 to-blue-600 rounded-3xl p-6 mb-6 text-white shadow-xl">
        <p className="text-sm text-white/70 mb-1">정산 가능 크레딧</p>
        <p className="text-4xl font-black mb-4">
          {stats.pendingCredit.toLocaleString()}
          <span className="text-xl font-bold ml-1">원</span>
        </p>
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { label: '누적 적립', value: `${stats.totalCredit.toLocaleString()}원` },
            { label: '정산 완료', value: `${stats.paidCredit.toLocaleString()}원` },
            { label: '유효 시청', value: `${stats.totalViews}회` },
          ].map(item => (
            <div key={item.label} className="bg-white/10 rounded-2xl p-3 text-center">
              <p className="text-xs text-white/60 mb-0.5">{item.label}</p>
              <p className="text-sm font-black">{item.value}</p>
            </div>
          ))}
        </div>

        <button
          onClick={() => canPayout && setShowPayout(true)}
          disabled={!canPayout}
          className={`w-full py-3 rounded-2xl font-black text-sm flex items-center justify-center gap-2 transition-all ${
            canPayout
              ? 'bg-white text-violet-700 hover:bg-white/90 shadow-md'
              : 'bg-white/20 text-white/50 cursor-not-allowed'
          }`}
        >
          <Banknote size={16} />
          {canPayout
            ? `${stats.pendingCredit.toLocaleString()}원 정산 신청`
            : `최소 ${MIN_PAYOUT.toLocaleString()}원 이상 적립 시 정산 가능`}
        </button>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: '등록 영상',   value: stats.videoCount, icon: <Youtube size={16} className="text-red-500" /> },
          { label: '총 시청수',   value: myVideos.reduce((s,v)=>s+v.viewCount,0), icon: <Eye size={16} className="text-blue-500" /> },
          { label: '유효 시청',   value: stats.totalViews, icon: <CheckCircle size={16} className="text-emerald-500" /> },
          { label: '평균 평점',
            value: myVideos.filter(v=>v.ratingCount>0).length > 0
              ? (myVideos.filter(v=>v.ratingCount>0).reduce((s,v)=>s+v.rating,0) /
                 myVideos.filter(v=>v.ratingCount>0).length).toFixed(1) + '★'
              : '—',
            icon: <Star size={16} className="text-yellow-500" /> },
        ].map(item => (
          <div key={item.label} className="bg-white border-2 border-slate-200 rounded-2xl p-4 text-center">
            <div className="flex justify-center mb-1">{item.icon}</div>
            <p className="text-xl font-black text-slate-800">{item.value}</p>
            <p className="text-xs text-slate-400 mt-0.5">{item.label}</p>
          </div>
        ))}
      </div>

      {/* 주간 시청 차트 (간단 바 차트) */}
      <div className="bg-white border-2 border-slate-200 rounded-2xl p-5 mb-6">
        <p className="text-sm font-black text-slate-700 mb-4 flex items-center gap-2">
          <TrendingUp size={14} className="text-blue-500" /> 주간 시청 추이
        </p>
        <div className="flex items-end gap-2 h-24">
          {weeklyViews.map((v, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex flex-col justify-end" style={{ height: '80px' }}>
                <div
                  className="w-full rounded-t-lg bg-gradient-to-t from-violet-400 to-blue-400 transition-all min-h-[4px]"
                  style={{ height: `${Math.max(4, (v / weekMax) * 80)}px` }}
                />
              </div>
              <span className="text-xs text-slate-400">{dayLabels[i]}</span>
            </div>
          ))}
        </div>
        {weeklyViews.every(v => v === 0) && (
          <p className="text-xs text-center text-slate-400 mt-2">아직 시청 데이터가 없습니다. 영상이 승인되면 데이터가 쌓입니다.</p>
        )}
      </div>

      {/* 크레딧 안내 */}
      <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-2xl p-4 mb-6">
        <p className="text-xs font-black text-violet-700 mb-2">💡 크레딧 계산 방식</p>
        <div className="grid grid-cols-2 gap-3 text-xs text-slate-600">
          <div className="flex items-start gap-2">
            <CheckCircle size={11} className="text-emerald-500 mt-0.5 flex-shrink-0" />
            <span>유효 시청 1회 = <strong className="text-violet-700">{CREDIT_PER_VIEW}원</strong></span>
          </div>
          <div className="flex items-start gap-2">
            <Clock size={11} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <span>최소 {MIN_WATCH_SEC}초 이상 시청</span>
          </div>
          <div className="flex items-start gap-2">
            <CheckCircle size={11} className="text-emerald-500 mt-0.5 flex-shrink-0" />
            <span>영상 50% 이상 시청해야 유효</span>
          </div>
          <div className="flex items-start gap-2">
            <AlertCircle size={11} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <span>같은 학생 하루 1회만 카운트</span>
          </div>
        </div>
      </div>

      {/* 내 영상 목록 */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-black text-slate-800">내 영상 목록</h2>
          <div className="flex gap-1">
            {(['all', 'pending', 'approved', 'rejected'] as const).map(s => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`text-xs px-2.5 py-1 rounded-full font-semibold transition-all ${
                  filterStatus === s
                    ? 'bg-violet-500 text-white'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {s === 'all' ? '전체' : s === 'pending' ? '대기' : s === 'approved' ? '승인' : '반려'}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-2xl">
            <Youtube size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-bold text-slate-400 mb-1">등록된 영상이 없습니다</p>
            <a
              href="/video-submit"
              className="text-sm text-violet-500 hover:text-violet-700 font-semibold"
            >
              + 첫 영상 등록하기
            </a>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(v => <VideoRow key={v.id} video={v} />)}
          </div>
        )}
      </div>

      {/* 정산 모달 */}
      {showPayout && (
        <PayoutModal
          pendingCredit={stats.pendingCredit}
          onClose={() => setShowPayout(false)}
        />
      )}
    </div>
  )
}
